import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ro from './locales/ro';
import en from './locales/en';

export const LOCALES = [
  { id: 'ro', tag: 'ro-RO', label: 'Română', short: 'RO' },
  { id: 'en', tag: 'en-GB', label: 'English', short: 'EN' },
];

export const DEFAULT_LOCALE = 'ro';

const DICTIONARIES = { ro, en };

const STORAGE_KEY = 'digestmed.locale';

const I18nContext = createContext(null);

const isKnown = (id) => LOCALES.some((l) => l.id === id);

const readStoredLocale = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isKnown(stored) ? stored : null;
  } catch (_) {
    return null;
  }
};

const lookup = (dict, key) =>
  key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), dict);

const interpolate = (template, params) =>
  template.replace(/{{\s*(\w+)\s*}}/g, (match, name) =>
    params[name] === undefined || params[name] === null
      ? match
      : String(params[name]),
  );

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(
    () => readStoredLocale() || DEFAULT_LOCALE,
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch (_) {
      // Persisting the choice is best-effort; the session still works.
    }
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((id) => {
    if (isKnown(id)) setLocaleState(id);
  }, []);

  const value = useMemo(() => {
    const tag = LOCALES.find((l) => l.id === locale).tag;
    const dict = DICTIONARIES[locale];
    const fallback = DICTIONARIES[DEFAULT_LOCALE];

    const t = (key, params) => {
      const template = lookup(dict, key) ?? lookup(fallback, key);
      if (typeof template !== 'string') return key;
      return params ? interpolate(template, params) : template;
    };

    const numberFormatter = new Intl.NumberFormat(tag);
    const dateTimeFormatter = new Intl.DateTimeFormat(tag, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const dateFormatter = new Intl.DateTimeFormat(tag, { dateStyle: 'medium' });

    const formatNumber = (n) => numberFormatter.format(n);
    const formatDateTime = (d) => {
      const date = d instanceof Date ? d : new Date(d);
      return Number.isNaN(date.getTime()) ? '' : dateTimeFormatter.format(date);
    };
    const formatDate = (d) => {
      const date = d instanceof Date ? d : new Date(d);
      return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date);
    };

    return {
      locale,
      localeTag: tag,
      setLocale,
      t,
      formatNumber,
      formatDate,
      formatDateTime,
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export function useTranslation() {
  return useI18n();
}
